/**
 * pages/signup/steps/Legal.tsx — signup step 1.
 *
 * Loads the 4 required legal documents from the backend, presents
 * each in a scrolling panel, and requires the user to tick each.
 * Every acceptance is POSTed to /v1/legal/accept synchronously
 * (before advancing to /signup/name) so the server-side gate on
 * /v1/identity/submit is guaranteed to see the acceptances.
 *
 * Draft-banner check: every doc coming out of the backend today
 * carries a "Draft — pending legal review" banner (see
 * product/legal.py). We don't hide it from the user — they should
 * SEE that we haven't finalised copy yet.
 */

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components";
import { getLegalDocuments, acceptLegal, type LegalDocument } from "../../../api/cip";
import { ApiClientError } from "../../../api/client";
import { advance } from "@aster/onboarding";
import { getProgress, setProgress } from "../signup-store";
import { SignupShell } from "../SignupShell";
import styles from "./Legal.module.css";

export function SignupLegal() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<LegalDocument[] | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load once — the doc list rarely changes, but every mount refetches
  // so a copy update on the server surfaces on the very next visit
  // without a browser cache dance.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getLegalDocuments();
        if (!cancelled) setDocs(d);
      } catch (e: unknown) {
        if (!cancelled) {
          setErr(
            e instanceof ApiClientError
              ? "We couldn't load the disclosures. Try again in a moment."
              : "Can't reach Aster right now. Check your connection.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allChecked =
    docs !== null &&
    docs.length > 0 &&
    docs.every((d) => checked[d.document_type]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!allChecked || pending || !docs) return;
    setPending(true);
    setErr(null);
    try {
      // Accept every doc — server is idempotent so re-accepting on
      // navigation-back doesn't duplicate rows.
      for (const d of docs) {
        await acceptLegal({
          document_type: d.document_type,
          document_version: d.document_version,
        });
      }
      const cur = getProgress() ?? {
        step: "legal" as const,
        written_at: new Date().toISOString(),
      };
      setProgress(advance(cur, "name", {
        legal_accepted_at: new Date().toISOString(),
      }));
      navigate("/signup/name");
    } catch (e: unknown) {
      if (e instanceof ApiClientError) {
        // `validation_failed` here almost always means a stale version
        // — the doc got updated between the fetch and the accept. Refetch.
        if (e.code === "validation_failed" && e.retryable) {
          setErr("The disclosures were updated. Refresh to see the latest.");
        } else if (e.code === "unauthenticated") {
          setErr("Please sign in first.");
          navigate("/signin");
        } else {
          setErr("Something went wrong. Try again in a moment.");
        }
      } else {
        setErr("Can't reach Aster right now.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <SignupShell
      title="Agree to the terms"
      subtitle="These are the four disclosures every AsterCash account needs before we can open one."
      step={1}
      errorBanner={err}
    >
      <form onSubmit={onSubmit} className={styles.form}>
        {docs === null ? (
          <p className={styles.loading}>Loading disclosures…</p>
        ) : (
          <>
            {docs.map((d) => (
              <details key={d.document_type} className={styles.doc}>
                <summary className={styles.summary}>
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      checked={!!checked[d.document_type]}
                      onChange={(e) =>
                        setChecked((prev) => ({
                          ...prev,
                          [d.document_type]: e.target.checked,
                        }))
                      }
                      aria-label={`I agree to the ${d.title}`}
                    />
                    <span className={styles.docTitle}>{d.title}</span>
                    <span className={styles.docVersion}>v{d.document_version}</span>
                  </label>
                </summary>
                <div className={styles.docBody}>
                  <pre className={styles.docContent}>{d.content_markdown}</pre>
                </div>
              </details>
            ))}
            <Button
              type="submit"
              disabled={!allChecked || pending}
              className={styles.submit}
            >
              {pending ? "Recording…" : "I agree — continue"}
            </Button>
          </>
        )}
      </form>
    </SignupShell>
  );
}
